import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { History, ShieldAlert } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import Pagination, { paginate } from "@/components/Pagination";
import { useAuth } from "@/context/AuthContext";

const actionColor = (a) => ({
  CREATE: "bg-emerald-100 text-emerald-800 border-emerald-200",
  UPDATE: "bg-blue-100 text-blue-800 border-blue-200",
  DELETE: "bg-rose-100 text-rose-800 border-rose-200",
}[a] || "bg-slate-100 text-slate-700 border-slate-200");

export default function Historique() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => { api.get("/historique").then((r) => setItems(r.data)); }, []);

  const filteredItems = items.filter((h) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (h.user_email || "").toLowerCase().includes(q) ||
      (h.action || "").toLowerCase().includes(q) ||
      (h.entity || "").toLowerCase().includes(q) ||
      (h.details || "").toLowerCase().includes(q)
    );
  });
  const pag = paginate(filteredItems, page);
  useEffect(() => { setPage(1); }, [search]);

  const isDevViewer = user?.role === "dev";

  return (
    <div>
      <PageHeader
        title="Historique"
        subtitle={isDevViewer
          ? "Journal d'audit complet — les interventions DEV sont visibles uniquement par les comptes DEV."
          : "Journal d'audit de toutes les actions effectuées sur la plateforme."
        }
        testid="page-historique"
        accent="amber"
        actions={
          <SearchInput value={search} onChange={setSearch} placeholder="Rechercher activité…" testid="search-historique" />
        }
      />

      <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Utilisateur</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Action</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Entité</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Détails</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-12">
                <History className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                {search ? `Aucun résultat pour « ${search} ».` : "Aucune activité."}
              </TableCell></TableRow>
            )}
            {pag.rows.map((h) => (
              <TableRow key={h.id} className="hover:bg-slate-50/60" data-testid={`row-hist-${h.id}`}>
                <TableCell className="text-slate-600 whitespace-nowrap">{formatDateTime(h.timestamp)}</TableCell>
                <TableCell className="text-slate-900 font-medium">
                  <div className="flex items-center gap-2">
                    {h.user_email}
                    {isDevViewer && h.actor_role === "dev" && (
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100 font-bold text-[10px] uppercase tracking-wider gap-1">
                        <ShieldAlert className="w-3 h-3" /> DEV
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={`${actionColor(h.action)} border hover:opacity-90`}>{h.action}</Badge>
                </TableCell>
                <TableCell className="text-slate-600 capitalize">{h.entity}</TableCell>
                <TableCell className="text-slate-600">{h.details || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination {...pag} onChange={setPage} testid="pagination-historique" />
    </div>
  );
}
